import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import Text from '../../components/Text';
import { t } from '../../i18n/langue';

/**
 * Les briques du dashboard mobile — #22, #87.
 *
 * ⚠️ Rendus **réécrits**, jamais réutilisés depuis `pages/dashboard/`. Ce n'est
 * pas un choix esthétique : **4 des 6 composants de vague 1 sont bâtis sur
 * `components/dataDisplay/Tabs`**, que #23 exclut du paquet mobile, et
 * `useModuleSize` mesure une *cellule de grille de bureau*, pas un téléphone.
 *
 * **Aucun onglet, nulle part.** En une colonne, un onglet cache la moitié du
 * module — on ne sait même pas qu'il y a autre chose.
 */

/** Le nombre de lignes d'un module liste — tranché à l'œil par #22. */
export const LIGNES_PAR_MODULE = 5;

export function Carte({
    titre,
    children,
    pied,
}: {
    titre: string;
    children: ReactNode;
    pied?: ReactNode;
}) {
    return (
        <section className="rounded-2xl bg-white px-4 py-3.5">
            <Text variante="sousTitre" as="h2" className="mb-2.5">
                {titre}
            </Text>
            {children}
            {pied && <div className="mt-2.5">{pied}</div>}
        </section>
    );
}

/** Un indicateur, dans la grille à deux colonnes. */
export function Indicateur({ valeur, libelle, dessous }: { valeur: string; libelle: string; dessous?: string }) {
    return (
        <div className="rounded-xl bg-main-bg-color px-3 py-2.5">
            <Text variante="titre" ton="marque" as="div" className="leading-tight">
                {valeur}
            </Text>
            <Text variante="petit" ton="attenue" className="block leading-snug">
                {libelle}
            </Text>
            {dessous && (
                <Text variante="petit" ton="attenue" className="block">
                    {dessous}
                </Text>
            )}
        </div>
    );
}

/**
 * Le pied de liste : il dit ce qu'on ne montre pas, **sans proposer de le montrer**.
 *
 * Un lien éjecterait hors de la PWA. La phrase nomme l'ordinateur parce que c'est
 * là que ça se consulte, et s'arrête là.
 *
 * ⚠️ Sur le module `orders`, cette phrase devient FAUSSE dès que l'écran
 * « Commandes » existe sur le téléphone (#40) — les autres sont alors à un
 * tapotement. Ce module passe donc un `pied` différent.
 */
export function PiedDeListe({ caches }: { caches: number }) {
    if (caches <= 0) return null;

    return (
        <Text variante="petit" ton="attenue">
            {t('dashboard.autresSurOrdinateur', { n: caches })}
        </Text>
    );
}

/**
 * Une ligne qui nomme quelqu'un, et ouvre sa feuille de détail.
 *
 * Correction actée par #22 en cours de ticket : la ligne menait directement à la
 * conversation. C'était une erreur d'appréciation sur le geste réel — **on lit
 * d'abord ce qui a été commandé, on écrit ensuite**. La conversation descend donc
 * d'un cran, dans la feuille.
 */
export function LignePersonne({
    nom,
    principal,
    secondaire,
    badge,
    onOuvrir,
}: {
    nom: string;
    principal: string;
    secondaire?: string;
    badge?: string;
    onOuvrir: () => void;
}) {
    const initiales = nom
        .split(' ')
        .map((mot) => mot[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('');

    return (
        // mobile-check-ignore — ligne de liste, même motif que la navigation du
        // panneau : le libellé accessible est le contenu lui-même.
        <button
            type="button"
            onClick={onOuvrir}
            aria-label={`${nom} — ${principal}`}
            className="flex min-h-touch w-full items-center gap-3 rounded-lg py-2.5 text-left"
        >
            <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-main-color-light text-sm font-semibold text-main-color-dark"
            >
                {initiales}
            </span>

            <span className="min-w-0 flex-1">
                <Text variante="petit" className="block truncate font-medium">
                    {nom}
                </Text>
                <Text variante="petit" ton="attenue" className="block truncate">
                    {principal}
                    {secondaire ? ` · ${secondaire}` : ''}
                </Text>
            </span>

            {badge && (
                <span className="shrink-0 rounded-full bg-main-color-light px-2 py-0.5">
                    <Text variante="petit" ton="marque">
                        {badge}
                    </Text>
                </span>
            )}

            <ChevronRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-text-color-light" />
        </button>
    );
}

/**
 * La carte de repli — module éligible mais non porté au mobile.
 *
 * **Sobre, sans chiffre, non tapotable**, et en carte PLEINE (#22) : la ligne
 * pointillée rompait la colonne, la carte pleine en garde le rythme.
 *
 * Le chiffre principal est écarté parce que l'afficher supposerait d'aller
 * chercher la donnée — c'est-à-dire de porter la moitié du module, ce que « non
 * porté » veut précisément dire. Un lien éjecterait hors de la PWA.
 *
 * Objet **rare** : 33 occurrences sur 12 dashboards dans toute la flotte, et aucun
 * dashboard n'est entièrement en repli.
 */
export function CarteDeRepli({ libelle }: { libelle: string }) {
    return (
        <section className="rounded-2xl border border-dashed border-border-color-light bg-white px-4 py-3.5">
            <Text variante="sousTitre" as="h2">
                {libelle}
            </Text>
            <Text variante="petit" ton="attenue" className="mt-1 block leading-snug">
                {t('dashboard.consultableSurOrdinateur')}
            </Text>
        </section>
    );
}
